import React, { useState } from 'react';
import { useNavigate }     from 'react-router-dom';
import {
  Form, Input, Select, DatePicker,
  Button, Card, Row, Col, message,
  Divider, Switch,
} from 'antd';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import employeeService from '../../services/employeeService';

const { Option } = Select;

const DIVISIONS = [
  'Cardiology','Neurology','Orthopedics','Oncology',
  'Pediatrics','General Medicine','Surgery','Gynecology',
];

const AAGMAN_OPTIONS = [
  { value: 'Pending',  label: 'Pending'  },
  { value: 'Attended', label: 'Attended' },
  { value: 'NA',       label: 'NA'       },
];

export default function AddEmployee() {
  const navigate = useNavigate();
  const [form]       = Form.useForm();
  const [loading,    setLoading]    = useState(false);
  const [aagmanVal,  setAagmanVal]  = useState(null);  // watch status for date field

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        dateOfJoining: values.dateOfJoining.format('YYYY-MM-DD'),
        callDate:      values.callDate?.format('YYYY-MM-DD'),
        cohortDate:    values.cohortDate?.format('YYYY-MM-DD') || null,
        cohortName:    values.cohortName || null,
        aagmanDate:    values.aagmanStatus === 'Attended'
                         ? (values.aagmanDate?.format('YYYY-MM-DD') || null)
                         : null,
        managerFollowup: {
          accompanied:          values.accompanied     ?? false,
          ipadProvided:         values.ipadProvided    ?? false,
          ipadStatus:           values.ipadStatus      || '',
          prathamCandidAtten:   values.prathamCandidAtten || '',
          prathamMgrAtten:      values.prathamMgrAtten    || '',
          mgrRemarks:           values.mgrRemarks         || '',
          prathamDate:          values.prathamDate?.format('YYYY-MM-DD') || null,
        },
      };

      // Remove flat followup fields — they're nested above
      delete payload.accompanied;
      delete payload.ipadProvided;
      delete payload.ipadStatus;
      delete payload.prathamCandidAtten;
      delete payload.prathamMgrAtten;
      delete payload.mgrRemarks;
      delete payload.prathamDate;

      await employeeService.create(payload);
      message.success('Employee added successfully!');
      navigate('/employees');
    } catch (err) {
      message.error(err.message || 'Failed to add employee');
    } finally {
      setLoading(false);
    }
  };

  const SectionTitle = ({ children }) => (
    <div style={{
      fontSize: 13, fontWeight: 700, color: '#1B2D6B',
      textTransform: 'uppercase', letterSpacing: 1,
      marginBottom: 16, marginTop: 4,
    }}>
      {children}
    </div>
  );

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/employees')}
        />
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a2d4a', margin: 0 }}>
            Add Employee
          </h2>
          <p style={{ fontSize: 13, color: '#7a96b0', margin: 0 }}>
            Enroll a new employee into the 180-day journey
          </p>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        requiredMark="optional"
        onValuesChange={(changed) => {
          if ('aagmanStatus' in changed) setAagmanVal(changed.aagmanStatus);
        }}
      >
        {/* ── Employee Info ── */}
        <Card style={{ borderRadius: 12, marginBottom: 16 }} bodyStyle={{ padding: '20px 24px' }}>
          <SectionTitle>Employee Information</SectionTitle>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="empCode"
                label="Employee Code"
                rules={[
                  { required: true, message: 'Required' },
                  { pattern: /^[A-Za-z]\d{4,6}$/, message: 'Format: L04480' },
                ]}
              >
                <Input placeholder="e.g. L04480" style={{ textTransform: 'uppercase' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="empName"
                label="Full Name"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="Employee full name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="designation" label="Designation">
                <Input placeholder="e.g. Sales Executive" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="division"
                label="Division"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select placeholder="Select division">
                  {DIVISIONS.map(d => <Option key={d} value={d}>{d}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="hq"
                label="HQ / Location"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="e.g. Mumbai" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="mobile"
                label="Mobile"
                rules={[
                  { required: true, message: 'Required' },
                  { pattern: /^\d{10}$/, message: '10-digit number' },
                ]}
              >
                <Input placeholder="10-digit mobile" maxLength={10} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="empEmail"
                label="Email"
                rules={[{ type: 'email', message: 'Invalid email' }]}
              >
                <Input placeholder="employee@erislifesciences.com" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="dateOfJoining"
                label="Date of Joining"
                rules={[{ required: true, message: 'Required' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Manager Info ── */}
        <Card style={{ borderRadius: 12, marginBottom: 16 }} bodyStyle={{ padding: '20px 24px' }}>
          <SectionTitle>Reporting Manager</SectionTitle>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="rmCode"
                label="RM Code"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="e.g. M01234" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="rmName"
                label="RM Name"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="Manager full name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="managerEmail"
                label="Manager Email"
                rules={[{ type: 'email', message: 'Invalid email' }]}
              >
                <Input placeholder="manager@erislifesciences.com" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="managerMobile"
                label="Manager Mobile"
                rules={[{ pattern: /^\d{10}$/, message: '10-digit number' }]}
              >
                <Input placeholder="10-digit mobile" maxLength={10} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Aagman / Onboarding ── */}
        <Card style={{ borderRadius: 12, marginBottom: 16 }} bodyStyle={{ padding: '20px 24px' }}>
          <SectionTitle>Onboarding Details</SectionTitle>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="aagmanStatus" label="Aagman Status">
                <Select placeholder="Select status">
                  {AAGMAN_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            {aagmanVal === 'Attended' && (
              <Col xs={24} sm={12} md={6}>
                <Form.Item
                  name="aagmanDate"
                  label="Attend Date"
                  rules={[{ required: true, message: 'Required when status is Attended' }]}
                >
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Select attend date" />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="callDate" label="Call Date (Day -1)">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="cohortName" label="Cohort Name">
                <Input placeholder="e.g. March 2026 Batch" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="cohortDate" label="Cohort Date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Manager Followup ── */}
        <Card style={{ borderRadius: 12, marginBottom: 24 }} bodyStyle={{ padding: '20px 24px' }}>
          <SectionTitle>Manager Follow-up</SectionTitle>
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="accompanied" label="Accompanied" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="ipadProvided" label="iPad Provided" valuePropName="checked">
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="ipadStatus" label="iPad Status">
                <Select placeholder="Select status" allowClear>
                  <Option value="Active">Active</Option>
                  <Option value="Pending">Pending</Option>
                  <Option value="Not Issued">Not Issued</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="prathamCandidAtten" label="Pratham Candidate Attendance">
                <Select placeholder="Select" allowClear>
                  <Option value="Present">Present</Option>
                  <Option value="Absent">Absent</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="prathamMgrAtten" label="Pratham Manager Attendance">
                <Select placeholder="Select" allowClear>
                  <Option value="Present">Present</Option>
                  <Option value="Absent">Absent</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="prathamDate" label="Pratham Date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Select Pratham date" />
              </Form.Item>
            </Col>
            <Col xs={24} md={18}>
              <Form.Item name="mgrRemarks" label="Manager Remarks">
                <Input.TextArea rows={3} placeholder="Any remarks from the manager..." />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* ── Actions ── */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button onClick={() => navigate('/employees')}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={loading}
            style={{ background: '#1B2D6B', minWidth: 140 }}
          >
            Save Employee
          </Button>
        </div>
      </Form>
    </div>
  );
}